/**
 * AnalysisComparisonPage
 * Side-by-side comparison of two deep analysis sessions
 */

import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, DeepAnalysisFullSession } from '../services/api';
import { getStatusLabel, getStatusBadgeClasses } from '../components/deep-analysis/utils/sessionMapper';
import ReactMarkdown from 'react-markdown';

type Tab = 'metadata' | 'report' | 'document';

export default function AnalysisComparisonPage() {
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';

  const session1Id = searchParams.get('session1');
  const session2Id = searchParams.get('session2');

  const [sessionA, setSessionA] = useState<DeepAnalysisFullSession | null>(null);
  const [sessionB, setSessionB] = useState<DeepAnalysisFullSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('metadata');

  useEffect(() => {
    if (!session1Id || !session2Id) {
      setError('Missing session IDs');
      setIsLoading(false);
      return;
    }

    const fetchSessions = async () => {
      try {
        setIsLoading(true);
        const [a, b] = await Promise.all([
          api.getDeepAnalysisSession(session1Id),
          api.getDeepAnalysisSession(session2Id),
        ]);
        setSessionA(a);
        setSessionB(b);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load analyses');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session1Id, session2Id]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (ms: number | null | undefined) => {
    if (!ms) return '—';
    const minutes = Math.round(ms / 60000);
    return `${minutes} ${t('analysisComparison.minutes')}`;
  };

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}k`;
    return String(tokens);
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: 'metadata', label: t('analysisComparison.metadata') },
    { key: 'report', label: t('analysisComparison.report') },
    { key: 'document', label: t('analysisComparison.document') },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <svg className="w-8 h-8 text-purple-500 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span className="ml-3 text-slate-500 dark:text-slate-400">{t('analysisComparison.loading')}</span>
      </div>
    );
  }

  if (error || !sessionA || !sessionB) {
    return (
      <div className="py-16 text-center">
        <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-red-600 dark:text-red-400">{error || t('analysisComparison.error')}</p>
        <Link
          to="/analysis/history"
          className="mt-4 inline-block px-4 py-2 text-sm text-purple-600 dark:text-purple-400 hover:underline"
        >
          {t('analysisComparison.backToHistory')}
        </Link>
      </div>
    );
  }

  const durationA = sessionA.completedAt
    ? new Date(sessionA.completedAt).getTime() - new Date(sessionA.createdAt).getTime()
    : null;
  const durationB = sessionB.completedAt
    ? new Date(sessionB.completedAt).getTime() - new Date(sessionB.createdAt).getTime()
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {t('analysisComparison.title')}
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {t('analysisComparison.subtitle')}
          </p>
        </div>
        <Link
          to="/analysis/history"
          className="px-4 py-2 text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {t('analysisComparison.backToHistory')}
        </Link>
      </div>

      {/* Differences summary */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4">
        <h2 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
          {t('analysisComparison.differences')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Token difference */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
            <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('analysisComparison.tokensDiff')}</p>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {(() => {
                  const diff = Math.abs(sessionA.tokensUsed.total - sessionB.tokensUsed.total);
                  if (diff < 100) return t('analysisComparison.sameTokens');
                  const label = sessionA.tokensUsed.total > sessionB.tokensUsed.total
                    ? t('analysisComparison.sessionA')
                    : t('analysisComparison.sessionB');
                  return t('analysisComparison.moreTokens', { label, value: formatTokens(diff) });
                })()}
              </p>
            </div>
          </div>

          {/* Duration difference */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('analysisComparison.durationDiff')}</p>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {(() => {
                  if (!durationA || !durationB) return '—';
                  const diff = Math.abs(durationA - durationB);
                  if (diff < 60000) return t('analysisComparison.sameDuration');
                  const label = durationA > durationB
                    ? t('analysisComparison.sessionA')
                    : t('analysisComparison.sessionB');
                  return t('analysisComparison.longerBy', { label, value: formatDuration(diff) });
                })()}
              </p>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('analysisComparison.status')}</p>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {sessionA.status === 'complete' && sessionB.status === 'complete'
                  ? t('analysisComparison.bothComplete')
                  : `${t(getStatusLabel(sessionA.status))} / ${t(getStatusLabel(sessionB.status))}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-1">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === tab.key
                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Side-by-side content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[
          { session: sessionA, label: t('analysisComparison.sessionA'), duration: durationA },
          { session: sessionB, label: t('analysisComparison.sessionB'), duration: durationB },
        ].map(({ session, label, duration }) => (
          <div
            key={session.id}
            className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden"
          >
            {/* Panel header */}
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</span>
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusBadgeClasses(session.status)}`}>
                {t(getStatusLabel(session.status))}
              </span>
            </div>

            <div className="p-4">
              {activeTab === 'metadata' && (
                <div className="space-y-3">
                  <MetadataRow label={t('analysisComparison.date')} value={formatDate(session.createdAt)} />
                  <MetadataRow label={t('analysisComparison.status')} value={t(getStatusLabel(session.status))} />
                  <MetadataRow label={t('analysisComparison.tokens')} value={formatTokens(session.tokensUsed.total)} />
                  <MetadataRow label={t('analysisComparison.duration')} value={formatDuration(duration)} />
                  <MetadataRow
                    label={t('analysisComparison.document')}
                    value={session.documentPreview || session.documentText.slice(0, 200) + '...'}
                    multiline
                  />
                </div>
              )}

              {activeTab === 'report' && (
                <div className="prose prose-sm dark:prose-invert max-w-none max-h-[600px] overflow-y-auto">
                  {session.finalReport ? (
                    <ReactMarkdown>{session.finalReport}</ReactMarkdown>
                  ) : (
                    <p className="text-slate-400 dark:text-slate-500 italic">
                      {t('analysisComparison.noReport')}
                    </p>
                  )}
                </div>
              )}

              {activeTab === 'document' && (
                <div className="max-h-[600px] overflow-y-auto">
                  <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                    {session.documentText}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetadataRow({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  return (
    <div className={multiline ? '' : 'flex items-center justify-between'}>
      <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
      <span className={`text-sm text-slate-700 dark:text-slate-300 ${multiline ? 'block mt-1' : ''}`}>
        {value}
      </span>
    </div>
  );
}
